import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDateTime } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { freqLabel } from "@/components/forms/fieldTypes";
import { ClipboardList, CheckCircle2, Lock, ChevronRight } from "lucide-react";

export default function MyForms() {
  const [forms, setForms] = useState(null);
  useEffect(() => { api.get("/forms/my").then((r) => setForms(r.data.forms)).catch(() => setForms([])); }, []);
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/form-submissions").then((r) => setSubs(r.data.slice(0, 20))).catch(() => {}); }, []);
  const canEdit = (s) => { const d = new Date(`${(s.submitted_at || "").slice(0, 10)}T00:00:00Z`); const t = new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`); return (t - d) / 86400000 <= 7; };
  const due = (forms || []).filter((f) => !f.done).length;
  return (
    <EmployeeLayout title="Forms">
      <div className="mb-4">
        <h1 className="text-xl font-heading font-bold text-slate-900">Forms & Checklists</h1>
        <p className="text-sm text-zinc-500" data-testid="my-forms-due-count">{due > 0 ? `${due} due` : "You're all caught up"}</p>
      </div>
      {forms && forms.length === 0 && <div className="text-center text-zinc-400 py-12" data-testid="my-forms-empty">No forms assigned to you.</div>}
      <div className="space-y-2">
        {(forms || []).map((f) => (
          <Link key={f.id} to={`/app/forms/${f.id}`} data-testid={`my-form-${f.id}`} className={`block bg-white border rounded-lg p-3 transition-colors hover:border-amber-400 ${!f.done && f.blocks_timesheet ? "border-amber-300" : "border-zinc-200"}`}>
            <div className="flex items-center gap-3">
              <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${f.done ? "bg-emerald-50" : "bg-amber-50"}`}>{f.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <ClipboardList className="h-4 w-4 text-amber-700" />}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{f.title}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1.5 flex-wrap">
                  <span>{freqLabel(f.frequency)}</span>
                  {f.done ? <span className="text-emerald-600 font-semibold" data-testid={`my-form-done-${f.id}`}>Done {f.frequency === "once" ? "" : f.due_label}</span>
                    : <span className="text-amber-700 font-semibold" data-testid={`my-form-due-${f.id}`}>Due {f.due_label}</span>}
                  {!f.done && f.blocks_timesheet && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 rounded"><Lock className="h-3 w-3" />Needed for timesheet</span>}
                </div>
                {f.last_submitted_at && <div className="text-[11px] text-zinc-400 mt-0.5">Last done {fmtDateTime(f.last_submitted_at)}</div>}
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-400" />
            </div>
          </Link>
        ))}
      </div>
      {subs.length > 0 && (
        <div className="mt-6" data-testid="my-submissions">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-2">My recent submissions</h2>
          <div className="space-y-2">
            {subs.map((s) => (
              <div key={s.id} data-testid={`my-sub-${s.id}`} className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm font-medium truncate">{s.form_title}</div><div className="text-xs text-zinc-500">{(s.submitted_at || "").slice(0, 10)}{s.edited_at ? " · edited" : ""}</div></div>
                {canEdit(s) ? <Link to={`/app/forms/sub/${s.id}`} data-testid={`my-sub-edit-${s.id}`} className="text-xs font-semibold text-amber-700 shrink-0">View / edit</Link>
                  : <Link to={`/app/forms/sub/${s.id}`} data-testid={`my-sub-view-${s.id}`} className="text-xs text-zinc-500 shrink-0">View</Link>}
              </div>
            ))}
          </div>
        </div>
      )}
    </EmployeeLayout>
  );
}
